import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Upload, Check, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ]
};
const quillFormats = ['header', 'bold', 'italic', 'strike', 'list', 'link'];

const slugifyPreview = (text) => {
  if (!text) return '';
  return text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
};

const UnitsManagement = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    location: '',
    property_type: 'Studio',
    bedrooms: 1,
    bathrooms: 1,
    area: '',
    price: 0,
    status: 'For Sale',
    featured: false,
    home_featured: false,
    images: [],
    amenities: [],
    building_name: '',
    description: '',
    location_coords: { lat: '', lng: '' },
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [amenitiesInput, setAmenitiesInput] = useState('');

  useEffect(() => {
    fetchUnits();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const fetchUnits = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/units`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUnits(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching units:', error);
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const token = localStorage.getItem('admin_token');
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${BACKEND_URL}/api/admin/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();
        uploadedUrls.push(data.url);
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error uploading images');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAmenitiesInput = () => {
    if (amenitiesInput.trim()) {
      // Split by comma, trim each item, and filter out empty strings
      const newAmenities = amenitiesInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
      
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, ...newAmenities]
      }));
      setAmenitiesInput('');
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const url = editingUnit
        ? `${BACKEND_URL}/api/admin/units/${editingUnit.id}`
        : `${BACKEND_URL}/api/admin/units`;

      const method = editingUnit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchUnits();
        resetForm();
        showNotification(editingUnit ? 'Unit updated successfully!' : 'Unit created successfully!', 'success');
      } else {
        if (response.status === 401) {
          showNotification('Session expired. Please log in again.', 'error');
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            window.location.href = '/admin';
          }, 2000);
        } else {
          showNotification('Error saving unit', 'error');
        }
      }
    } catch (error) {
      console.error('Error saving unit:', error);
      showNotification('Error saving unit: ' + error.message, 'error');
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name,
      slug: unit.slug || '',
      location: unit.location,
      property_type: unit.property_type,
      bedrooms: unit.bedrooms,
      bathrooms: unit.bathrooms,
      area: unit.area,
      price: unit.price,
      status: unit.status,
      featured: unit.featured,
      home_featured: unit.home_featured || false,
      images: unit.images || [],
      amenities: unit.amenities || [],
      building_name: unit.building_name || '',
      description: unit.description || '',
      location_coords: unit.location_coords || { lat: '', lng: '' },
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/units/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchUnits();
        showNotification('Unit deleted successfully!', 'success');
      } else {
        if (response.status === 401) {
          showNotification('Session expired. Please log in again.', 'error');
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            window.location.href = '/admin';
          }, 2000);
        } else {
          showNotification('Error deleting unit', 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting unit:', error);
      showNotification('Error deleting unit: ' + error.message, 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      location: '',
      property_type: 'Studio',
      bedrooms: 1,
      bathrooms: 1,
      area: '',
      price: 0,
      status: 'For Sale',
      featured: false,
      home_featured: false,
      images: [],
      amenities: [],
      building_name: '',
      description: '',
      location_coords: { lat: '', lng: '' },
    });
    setEditingUnit(null);
    setShowForm(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="p-8">
      {/* Success/Error Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-in slide-in-from-top ${
          notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {notification.type === 'success' && <CheckCircle className="h-5 w-5" />}
          {notification.type === 'error' && <X className="h-5 w-5" />}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Units Management</h1>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#20B2AA] hover:bg-[#189a93] text-white"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Unit
        </Button>
      </div>

      {/* Units Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#20B2AA] border-t-transparent"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Featured
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Homepage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {units.map((unit) => (
                <tr key={unit.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <img
                          className="h-10 w-10 rounded-lg object-cover"
                          src={unit.images[0] || 'https://via.placeholder.com/40'}
                          alt={unit.name}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{unit.name}</div>
                        <div className="text-sm text-gray-500">{unit.property_type}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {unit.location}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {unit.bedrooms}BR / {unit.bathrooms}BA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatPrice(unit.price)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      unit.status === 'For Sale' ? 'bg-green-100 text-green-800' :
                      unit.status === 'For Leasing' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {unit.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {unit.featured ? (
                      <span className="text-yellow-600 font-semibold">★ Featured</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {unit.home_featured ? (
                      <span className="text-green-600 font-semibold">🏠 Yes</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(unit)}
                      className="text-[#20B2AA] hover:text-[#189a93] mr-4"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(unit.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingUnit ? 'Edit Unit' : 'Add New Unit'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                {/* SEO Slug (full width) */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SEO URL Slug
                  </label>
                  <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#20B2AA]">
                    <span className="px-3 py-2 bg-gray-100 text-gray-500 text-sm border-r border-gray-300 whitespace-nowrap">
                      /units/
                    </span>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: slugifyPreview(e.target.value) })}
                      placeholder={slugifyPreview(formData.name) || 'auto-generated-from-name'}
                      className="flex-1 px-4 py-2 focus:outline-none"
                      data-testid="units-slug-input"
                    />
                    {!formData.slug && formData.name && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, slug: slugifyPreview(formData.name) })}
                        className="px-3 py-2 text-xs text-[#20B2AA] hover:bg-[#20B2AA]/10 border-l border-gray-300"
                      >
                        Auto
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Custom URL for SEO. Only lowercase letters, numbers, and dashes. Leave blank to auto-generate from name.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Type *
                  </label>
                  <select
                    required
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  >
                    <option value="Studio">Studio</option>
                    <option value="1 Bed/Bath">1 Bed/Bath</option>
                    <option value="2Bed/Bath">2Bed/Bath</option>
                    <option value="3Bed/Bath">3Bed/Bath</option>
                    <option value="4Bed/Bath">4Bed/Bath</option>
                    <option value=">4 Bed/Bath">&gt;4 Bed/Bath</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Building Name
                  </label>
                  <input
                    type="text"
                    value={formData.building_name}
                    onChange={(e) => setFormData({ ...formData, building_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bedrooms *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bathrooms *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Area (e.g., "1200 sq ft") *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (USD) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  >
                    <option value="For Sale">For Sale</option>
                    <option value="For Leasing">For Leasing</option>
                    <option value="Sold Out">Sold Out</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-5 h-5 text-[#20B2AA] border-gray-300 rounded focus:ring-[#20B2AA]"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Mark as Featured
                    </span>
                  </label>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.home_featured}
                      onChange={(e) => setFormData({ ...formData, home_featured: e.target.checked })}
                      className="w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Feature on Homepage
                    </span>
                  </label>
                </div>
              </div>

              {/* Location Coordinates */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location Coordinates (for Map)
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude (e.g., 40.7178)"
                    value={formData.location_coords.lat}
                    onChange={(e) => setFormData({ ...formData, location_coords: { ...formData.location_coords, lat: parseFloat(e.target.value) || '' } })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude (e.g., -74.0431)"
                    value={formData.location_coords.lng}
                    onChange={(e) => setFormData({ ...formData, location_coords: { ...formData.location_coords, lng: parseFloat(e.target.value) || '' } })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>
              </div>

              {/* Description (Rich Text) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Rich Text)
                </label>
                <div className="border border-gray-300 rounded-lg quill-container">
                  <ReactQuill
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    placeholder="Describe this unit... Use the toolbar for formatting."
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Use H1, H2, H3 for headings. Bold, Italic, Strike, and Links available.</p>
                <style>{`
                  .quill-container { position: relative; }
                  .quill-container .ql-picker-options { z-index: 9999 !important; position: absolute !important; }
                  .quill-container .ql-header .ql-picker-options {
                    background: white; border: 1px solid #ccc; border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                  }
                `}</style>
              </div>

              {/* Images Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Images
                </label>
                <div className="mb-4">
                  <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#20B2AA] transition-colors">
                    <div className="text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600">
                        {uploadingImages ? 'Uploading...' : 'Click to upload images'}
                      </p>
                    </div>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="hidden"
                    />
                  </label>
                </div>

                {formData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-4">
                    {formData.images.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Unit ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amenities
                </label>
                <div className="mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={amenitiesInput}
                      onChange={(e) => setAmenitiesInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAmenitiesInput())}
                      placeholder="Add amenities separated by commas (e.g., Parking, Gym, Pool)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                    />
                    <Button
                      type="button"
                      onClick={handleAmenitiesInput}
                      className="bg-[#20B2AA] hover:bg-[#189a93] text-white"
                    >
                      <Plus className="h-5 w-5" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Separate multiple amenities with commas</p>
                </div>

                {formData.amenities.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.amenities.map((amenity, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"
                      >
                        <span className="text-sm text-gray-700">{amenity}</span>
                        <button
                          type="button"
                          onClick={() => removeAmenity(index)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#20B2AA] hover:bg-[#189a93] text-white px-6"
                >
                  {editingUnit ? 'Update Unit' : 'Create Unit'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitsManagement;
